import React, { useEffect, useState, useCallback } from "react";
import { Spin } from "antd";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { GET_OPERATIONS_DASHBOARD } from "../../../Queries/OperationsDashboard";
import { GET_SALES_DASHBOARD } from "../../../Queries/SalesDashboard";
import { T, SelfDashboard, SectionHeader, RefreshButton, DatePickerBar } from "./OpsDashboardShared";
import { SalesSelfDashboard } from "./SalesDashboard";

const MyDashboard = () => {
  const logindata = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const userId = logindata?._id || logindata?.id;
  const userDepartment = (logindata?.department || "").toLowerCase();
  const additionalRoles = (logindata?.additionalRoles || []).map((r) => r.toLowerCase());
  const isSales = userDepartment === "sales" || additionalRoles.includes("sales");
  const isOps = userDepartment !== "sales"; // all non-sales departments see ops metrics
  const [selectedDate, setSelectedDate] = useState(null);

  const [fetchOps, { data: opsData, loading: opsLoading, error: opsError }] = useLazyQuery(GET_OPERATIONS_DASHBOARD, {
    fetchPolicy: "network-only",
  });

  const [fetchSales, { data: salesData, loading: salesLoading }] = useLazyQuery(GET_SALES_DASHBOARD, {
    fetchPolicy: "network-only",
  });

  const loadData = useCallback((date) => {
    if (!userId) return;
    const variables = { userId, view: "self" };
    if (date) variables.date = date;
    // Ops query has general work metrics (hours, activity, desktop tracking) — fetch for everyone
    fetchOps({ variables });
    // Sales query only for sales department or additional sales roles
    if (isSales) fetchSales({ variables });
  }, [userId, isSales, fetchOps, fetchSales]);

  useEffect(() => {
    loadData(selectedDate);
  }, [userId]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    loadData(date);
  };

  const opsEmp = opsData?.getOperationsDashboard?.employees?.[0] || null;
  const salesEmp = isSales ? salesData?.getSalesDashboard?.employees?.[0] : null;
  const loading = opsLoading || salesLoading;

  return (
    <div style={{ background: T.bg, minHeight: "100vh", padding: "24px 28px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: "-0.5px" }}>
            My Dashboard
          </h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: "4px 0 0" }}>
            Your personal work metrics and activity overview
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <DatePickerBar value={selectedDate} onChange={handleDateChange} />
          <RefreshButton loading={loading} onClick={() => loadData(selectedDate)} />
        </div>
      </div>

      {/* Loading */}
      {loading && !opsData && !salesData ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Sales Section — only for Sales department */}
          {isSales && salesEmp && (
            <>
              <SectionHeader title="Sales Performance" badge="Sales" badgeColor={T.violet} subtitle="Your leads, outreach, and pipeline" />
              <div style={{ marginBottom: 32 }}>
                <SalesSelfDashboard emp={salesEmp} opsEmp={opsEmp} />
              </div>
            </>
          )}

          {/* Operations Section — only for non-Sales departments */}
          {isOps && opsEmp && (
            <SelfDashboard emp={opsEmp} />
          )}

          {/* Error loading ops data */}
          {opsError && !opsEmp && (
            <div className="ops-card" style={{ padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>!</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>Failed to load dashboard</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>{opsError.message}</div>
              <button className="ops-refresh-btn" onClick={() => loadData(selectedDate)}>Try Again</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyDashboard;
